import React, { useEffect, useMemo, useState } from "react"
import { apiFetch } from "../utils/apiFetch"
import "../shared/styles/settings-system.css"

export default function DoctorTreatmentMatrix() {
  const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")
  const [userId] = useState("e922ef71-7119-4f8c-8b73-3c8c42629ebd")

  const [clinics, setClinics] = useState([])
  const [clinicId, setClinicId] = useState("")
  const [doctors, setDoctors] = useState([])
  const [treatments, setTreatments] = useState([])
  const [permissionsMap, setPermissionsMap] = useState({})
  const [savingDoctorId, setSavingDoctorId] = useState("")

  const specialtyLabel = (value) => {
    const map = {
      GENERAL: "General",
      ORTODONCIA: "Ortodoncia",
      ENDODONCIA: "Endodoncia",
      CIRUGIA: "CirugÃ­a",
      IMPLANTOLOGIA: "ImplantologÃ­a",
      PROSTODONCIA: "Prostodoncia",
      PERIODONCIA: "Periodoncia",
      PEDIATRIA: "OdontopediatrÃ­a"
    }
    return map[value] || "Sin especialidad"
  }

  const requiredSpecialtyLabel = (value) => {
    if (!value) return "Libre"
    return specialtyLabel(value)
  }

  const permissionReason = (doctor, treatment, checked) => {
    if (checked) {
      return {
        label: "Permiso explÃ­cito",
        color: "#166534",
        bg: "#dcfce7",
        border: "#86efac"
      }
    }

    if (!treatment.requiredSpecialty) {
      return {
        label: "Libre",
        color: "#334155",
        bg: "#f8fafc",
        border: "#e5e7eb"
      }
    }

    if (doctor?.specialty === treatment.requiredSpecialty) {
      return {
        label: "Por especialidad",
        color: "#1d4ed8",
        bg: "#dbeafe",
        border: "#93c5fd"
      }
    }

    return {
      label: "No compatible",
      color: "#9a3412",
      bg: "#fff7ed",
      border: "#fdba74"
    }
  }

  const loadClinics = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/multi-clinic/user-clinics?userId=${userId}`)
      const data = await res.json().catch(() => [])
      const rows = Array.isArray(data) ? data : []
      setClinics(rows)

      if (!clinicId && rows.length > 0) {
        setClinicId(rows[0].clinicId)
      }
    } catch {
      setClinics([])
    }
  }

  const loadDoctors = async (cId = clinicId) => {
    try {
      if (!cId) return
      const res = await apiFetch(`${API_BASE}/multi-clinic/clinic-doctors?clinicId=${cId}`)
      const data = await res.json().catch(() => [])
      setDoctors(Array.isArray(data) ? data : [])
    } catch {
      setDoctors([])
    }
  }

  const loadTreatments = async (cId = clinicId) => {
    try {
      if (!cId) return
      const res = await apiFetch(`${API_BASE}/treatments?clinicId=${cId}`)
      const data = await res.json().catch(() => [])
      setTreatments(Array.isArray(data) ? data : [])
    } catch {
      setTreatments([])
    }
  }

  const loadPermissionsForDoctors = async (doctorRows) => {
    try {
      const map = {}

      for (const doctor of doctorRows) {
        const res = await apiFetch(
          `${API_BASE}/doctor-treatments/permissions?doctorId=${doctor.id}`
        )
        const data = await res.json().catch(() => [])
        map[doctor.id] = Array.isArray(data)
          ? data.map((x) => x.treatmentId)
          : []
      }

      setPermissionsMap(map)
    } catch {
      setPermissionsMap({})
    }
  }

  useEffect(() => {
    loadClinics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const run = async () => {
      if (!clinicId) return
      await loadDoctors(clinicId)
      await loadTreatments(clinicId)
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId])

  useEffect(() => {
    if (!doctors.length) {
      setPermissionsMap({})
      return
    }
    loadPermissionsForDoctors(doctors)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctors])

  const togglePermission = (doctorId, treatmentId) => {
    setPermissionsMap((prev) => {
      const current = prev[doctorId] || []
      const exists = current.includes(treatmentId)

      return {
        ...prev,
        [doctorId]: exists
          ? current.filter((id) => id !== treatmentId)
          : [...current, treatmentId]
      }
    })
  }

  const saveDoctorPermissions = async (doctorId) => {
    try {
      setSavingDoctorId(doctorId)

      const res = await apiFetch(`${API_BASE}/doctor-treatments/permissions/replace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          doctorId,
          clinicId,
          treatmentIds: permissionsMap[doctorId] || []
        })
      })

      if (!res.ok) {
        alert("No se pudieron guardar los permisos del doctor")
        return
      }

      alert("Permisos guardados")
    } catch {
      alert("Error al guardar permisos")
    } finally {
      setSavingDoctorId("")
    }
  }

  const matrixRows = useMemo(() => {
    return treatments.map((treatment) => ({
      ...treatment,
      doctors: doctors.map((doctor) => ({
        doctorId: doctor.id,
        checked: (permissionsMap[doctor.id] || []).includes(treatment.id)
      }))
    }))
  }, [treatments, doctors, permissionsMap])

  return (
    <div className="of-settings-page">
      <div className="of-settings-hero">
        <h1 className="of-settings-hero__title">Matriz doctor Ã— tratamiento</h1>
        <div className="of-settings-hero__subtitle">
          Administra visualmente quÃ© tratamientos puede realizar cada doctor y por quÃ©.
        </div>
      </div>

      <div className="of-settings-grid">
        <div className="of-settings-card">
          <div className="of-settings-topbar">
            <div>
              <div className="of-settings-card__title">ClÃ­nica y volumen</div>
              <div className="of-settings-card__subtitle">
                Doctores y tratamientos cargados para la matriz.
              </div>
            </div>

            <div className="of-settings-actions">
              <select
                value={clinicId}
                onChange={(e) => setClinicId(e.target.value)}
                className="of-settings-select"
                style={{ minWidth: 260 }}
              >
                {clinics.map((c) => (
                  <option key={c.id} value={c.clinicId}>
                    {c.clinic?.name || c.clinicName || c.name || "ClÃ­nica"}
                  </option>
                ))}
              </select>

              <div className="of-settings-pill">
                Doctores: {doctors.length} Â· Tratamientos: {treatments.length}
              </div>
            </div>
          </div>
        </div>

        <div className="of-settings-card">
          <div className="of-settings-summary">
            <div className="of-settings-pill">Permiso explÃ­cito</div>
            <div className="of-settings-pill">Permitido por especialidad</div>
            <div className="of-settings-pill">Tratamiento libre</div>
            <div className="of-settings-pill">No compatible</div>
          </div>
        </div>

        <div className="of-settings-card" style={{ overflowX: "auto" }}>
          <div style={{ minWidth: Math.max(900, 320 + doctors.length * 240) }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `320px repeat(${doctors.length}, 240px)`,
                gap: 8,
                alignItems: "stretch",
                marginBottom: 10
              }}
            >
              <div className="of-settings-item" style={{ background: "#eef2f7" }}>
                <div className="of-settings-item__title">Tratamiento</div>
              </div>

              {doctors.map((doctor) => (
                <div
                  key={doctor.id}
                  className="of-settings-item"
                  style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}
                >
                  <div className="of-settings-item__title">{doctor.name}</div>
                  <div className="of-settings-item__meta">
                    Especialidad: {specialtyLabel(doctor.specialty)}
                  </div>

                  <button
                    onClick={() => saveDoctorPermissions(doctor.id)}
                    disabled={savingDoctorId === doctor.id}
                    className="of-settings-button of-settings-button--info"
                    style={{ marginTop: 10, width: "100%" }}
                  >
                    {savingDoctorId === doctor.id ? "Guardando..." : "Guardar permisos"}
                  </button>
                </div>
              ))}
            </div>

            <div className="of-settings-list">
              {matrixRows.map((row) => (
                <div
                  key={row.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: `320px repeat(${doctors.length}, 240px)`,
                    gap: 8,
                    alignItems: "stretch"
                  }}
                >
                  <div className="of-settings-item" style={{ background: "#f8fafc" }}>
                    <div className="of-settings-item__title">{row.name}</div>
                    <div className="of-settings-item__meta">
                      Requiere: {requiredSpecialtyLabel(row.requiredSpecialty)}
                    </div>
                    <div className="of-settings-item__meta">
                      {row.durationMinutes} min Â· ${row.price}
                    </div>
                  </div>

                  {row.doctors.map((cell) => {
                    const doctor = doctors.find((d) => d.id === cell.doctorId)
                    const reason = permissionReason(doctor, row, cell.checked)

                    return (
                      <label
                        key={`${row.id}-${cell.doctorId}`}
                        className="of-settings-item"
                        style={{
                          background: reason.bg,
                          borderColor: reason.border,
                          cursor: "pointer"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={cell.checked}
                            onChange={() => togglePermission(cell.doctorId, row.id)}
                          />
                          <span style={{ fontWeight: 700 }}>
                            {cell.checked ? "Permitido" : "Sin permiso"}
                          </span>
                        </div>

                        <div
                          className="of-settings-pill"
                          style={{
                            width: "fit-content",
                            color: reason.color,
                            borderColor: reason.border,
                            background: "#ffffffaa"
                          }}
                        >
                          {reason.label}
                        </div>

                        <div className="of-settings-item__meta">
                          {reason.label === "Permiso explÃ­cito" &&
                            "Este doctor podrÃ¡ realizarlo aunque no coincida su especialidad."}
                          {reason.label === "Por especialidad" &&
                            "Se permite por coincidencia entre especialidad del doctor y requisito del tratamiento."}
                          {reason.label === "Libre" &&
                            "Este tratamiento no exige especialidad obligatoria."}
                          {reason.label === "No compatible" &&
                            "No coincide la especialidad del doctor y tampoco hay permiso explÃ­cito."}
                        </div>
                      </label>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
